package nl.volleyanalyzer.web.controller

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.launch
import kotlinx.datetime.format
import kotlinx.html.*
import kotlinx.html.js.onClickFunction
import nl.volleyplanner.api.sports.TeamIdentifier
import nl.volleyplanner.api.sports.TeamMatchResult
import nl.volleyplanner.web.DATE_TIME_TEXT_SHORT_NAMES
import nl.volleyplanner.web.base.HTMLComponent
import nl.volleyplanner.web.base.component.BootstrapButtonStyle
import nl.volleyplanner.web.base.component.ModalComponent
import nl.volleyplanner.web.base.component.iconButton
import org.w3c.dom.HTMLElement

class PouleResultsComparisonComponent(private val pouleMatchesData: PouleMatchesData) : HTMLComponent() {
    companion object {
        private const val COL_MARGIN = "me-2"
        private const val POULE_MIN_WIDTH = "150px"
        private const val POULE_MAX_WIDTH = "300px"
        private const val TEAM_MIN_WIDTH = "260px"
        private const val TEAM_MAX_WIDTH = "450px"
    }

    private val comparisonTeamsState = MutableStateFlow(listOf<String>())

    fun triggerAddTeamModal() {
        val modal = object : ModalComponent("Selecteer") {
            override fun FlowContent.renderBody() {
                pouleMatchesData.standings!!.lastRoundStandings()
                    .records
                    .keys
                    .filter { !comparisonTeamsState.value.contains(it) }
                    .forEach { selectableTeam ->
                        div (classes = "row p-2") {
                            a (href = "#") {
                                onClickFunction = { event ->
                                    event.preventDefault()
                                    comparisonTeamsState.value += selectableTeam
                                    dispose()
                                }
                                +selectableTeam
                            }
                        }
                    }
            }
        }
        CoroutineScope(Dispatchers.Main).launch {
            createStandaloneComponent(modal).show()
        }
    }

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        if (pouleMatchesData.standings == null) {
            return consumer.div { +"No results found" }
        }
        val comparisonTeams = comparisonTeamsState.value
        val multiRoundPouleStandings = pouleMatchesData.standings
        return consumer.div (classes = "overflow-x-scroll") {
            div (classes = "row flex-nowrap m-0") {
                div (classes = "col border border-secondary rounded-top p-2 align-items-center") {
                    classes += COL_MARGIN
                    style = "min-width: $POULE_MIN_WIDTH; max-width: $POULE_MAX_WIDTH;"
                    h6 { +"Poule" }
                }
                if (comparisonTeams.isEmpty()) {
                    div (classes = "col border border-secondary rounded-top p-2 d-inline align-items-center") {
                        classes += COL_MARGIN
                        style = "min-width: $TEAM_MIN_WIDTH; max-width: $TEAM_MAX_WIDTH;"
                        span (classes = "m-2") {
                            h6(classes = "me-2 d-inline") {
                                a (href="#") {
                                    onClickFunction = { event ->
                                        event.preventDefault()
                                        triggerAddTeamModal()
                                    }
                                    +"Selecteer team"
                                }
                            }
                        }
                    }
                }
                comparisonTeams.forEach { team ->
                    div (classes = "col border border-secondary rounded-top p-2 d-inline align-items-center") {
                        classes += COL_MARGIN
                        style = "min-width: $TEAM_MIN_WIDTH; max-width: $TEAM_MAX_WIDTH;"
                        span (classes = "m-2") {
                            h6(classes = "me-2 d-inline") { +team }
                            iconButton(BootstrapButtonStyle.Secondary, icon = "bi-trash") { event ->
                                event.preventDefault()
                                comparisonTeamsState.value -= team
                            }
                        }
                    }
                }
                div (classes = "col-auto p-0") {
                    style = "width: 40px"
                    iconButton(BootstrapButtonStyle.Primary, icon = "bi-plus") { event ->
                        event.preventDefault()
                        triggerAddTeamModal()
                    }
                }
            }
            var index = 1
            multiRoundPouleStandings.lastRoundStandings()
                .records
                .forEach { (teamName, standingsRecord) ->
                    div (classes = "row flex-nowrap m-0") {
                        div (classes = "col border border-secondary border-top-0 p-2 align-items-center position-sticky") {
                            classes += COL_MARGIN

                            style = "min-width: $POULE_MIN_WIDTH; max-width: $POULE_MAX_WIDTH;"
                            div (classes = "row") {
                                div (classes = "col") {
                                    strong { +"${index++}. $teamName" }
                                }
                            }
                            div (classes = "row") {
                                div (classes = "col small") {
                                    strong { +"${standingsRecord.points}" }
                                    +" punten, "
                                    strong { +"${standingsRecord.matchesPlayed}" }
                                    +" gespeeld"
                                }
                            }
                        }
                        if (comparisonTeams.isEmpty()) {
                            div (classes = "col p-0") {
                                classes += COL_MARGIN
                                style = "min-width: $TEAM_MIN_WIDTH; max-width: $TEAM_MAX_WIDTH;"
                            }
                        }
                        comparisonTeams.forEach { comparisonTeam ->
                            val matches = if (teamName != comparisonTeam) {
                                pouleMatchesData.pouleMatchesResponse.matches
                                    .filter { it.hasTeam(teamName) && it.hasTeam(comparisonTeam) }
                                    .sortedBy { it.startDateTime }
                            } else emptyList()
                            div (classes = "col border border-secondary border-top-0 p-0") {
                                classes += COL_MARGIN

                                style = "min-width: $TEAM_MIN_WIDTH; max-width: $TEAM_MAX_WIDTH;"
                                if (teamName == comparisonTeam) {
                                    classes += "bg-secondary-subtle"
                                }

                                matches.forEachIndexed { index, match ->
                                    val pouleTeamId = match.identifyTeam(teamName)
                                    val comparisonTeamId = match.identifyTeam(comparisonTeam)
                                    div (classes = "row m-0") {
                                        if (index != 0) {
                                            classes = classes + setOf("border-top border-secondary-subtle")
                                        }
                                        div (classes = "col-2 border-end border-secondary-subtle fw-bolder p-2") {
                                            span (classes="align-middle") {
                                                +"#${index + 1}"
                                            }
                                        }
                                        div (classes = "col-10") {
                                            if (match.hasScore()) {
                                                classes += when (match.getTeamResult(comparisonTeamId)) {
                                                    TeamMatchResult.Win -> "bg-success-subtle"
                                                    TeamMatchResult.Lose -> "bg-danger-subtle"
                                                    TeamMatchResult.Tie -> "bg-warning-subtle"
                                                }
                                            }
                                            div (classes = "row my-1") {
                                                div (classes = "col") {
                                                    if (match.hasScore()) {
                                                        classes += "text-secondary"
                                                    }
                                                    val uitThuisText = when (comparisonTeamId) {
                                                        TeamIdentifier.Home -> "thuis"
                                                        TeamIdentifier.Guest -> "uit"
                                                    }
                                                    +(match.startDateTime?.format(DATE_TIME_TEXT_SHORT_NAMES) ?: "ntb")
                                                    +" ($uitThuisText)"
                                                }
                                            }
                                            div (classes = "row my-1 align-items-center") {
                                                div (classes = "col-auto") {
                                                    if (match.hasScore()) {
                                                        matchSetResultBadge(match, comparisonTeamId)
                                                    }
                                                }
                                                div (classes = "col px-0") {
                                                    if (match.hasScore()) {
                                                        setResultsBadges(match, comparisonTeamId)
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        div (classes = "col-auto") {
                            style = "width: 40px"
                        }
                    }
                }
        }
    }

    override suspend fun getState() = comparisonTeamsState
}